import { LitElement, html, css } from '@hh/global.js'

class HHInputWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: {
      reflect: true,
    },
    filled: {
      reflect: true,
    },
    label: {},
    mouseover: {
      reflect: true,
    },
    mousedown: {
      reflect: true,
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    this.$select = this.querySelector(`select[slot='input']`)
    this.$select && this.preselectSelectValue()

    if (!!this.querySelector(`[slot='input']`).value && !this.$select) {
      this.filled = true
    }

    if (this.$select && this.label !== 'stacked') {
      this.filled = true
    }

    this.addEventListener('keydown', this.onKeyDown.bind(this))
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default
    }
  }

  onChange(e) {
    if (this.animation === 'none' || this.label === 'stacked') {
      return
    }
    this.filled = !!e.target.value
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return
    }

    if (this.mousedown) {
      this.focused = false
    } else {
      this.focused = true
    }
  }

  onMouseDown(e) {
    this.mousedown = true
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = false
    this.mousedown = false
  }

  onMouseOver(e) {
    this.mouseover = true
  }

  onMouseLeave(e) {
    this.mouseover = false
  }

  onKeyDown(e) {
    if (e.key === 'Enter') {
      this.focused = false
    }
  }

  render() {
    return html`
      <slot name="label"></slot>
      <slot name="icon"></slot>
      <slot
        name="input"
        @mousedown="${this.onMouseDown}"
        @input="${this.onChange}"
        @keydown="${this.onChange}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
        @mouseover="${this.onMouseOver}"
        @mouseleave="${this.onMouseLeave}"
        autofocus="false"
      ></slot>
    `
  }

  static styles = css`
    ::slotted(label) {
      position: absolute;
      height: var(--hh-height, 45px);
      line-height: var(--hh-height, 45px);
      padding-left: var(--hh-left-indent-small, 4px);
      color: var(--hh-color-subdued);
      transform: translateY(0);
      transition: transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
      pointer-events: none;
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      width: 100%;
      height: var(--hh-height, 45px);
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    ::slotted(input) {
      top: 14px;
      height: var(--hh-height, 34px);
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      z-index: 2;
      text-indent: var(--hh-left-indent-small, 4px);
      font: var(--hh-font);
      color: currentColor;
      background: transparent;
      border: 0;
      padding: 0;
    }

    ::slotted(select) {
      appearance: none;
    }

    ::slotted(input),
    ::slotted(textarea) {
      outline: 0 !important;
    }

    ::slotted(textarea) {
      text-indent: var(--hh-left-indent-small, 0);
      padding: 0 var(--hh-left-indent-small, 4px);
      padding-top: 27px;
      resize: none;
      box-sizing: border-box;
    }

    :host *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    // :host([focused='true']) {
    //   outline: 0.2rem solid var(--hh-color-focus);
    //   outline-offset: 0;
    // }

    :host([focused='true']) ::slotted(label),
    :host([filled='true']) ::slotted(label),
    :host([mousedown='true']) ::slotted(label) {
      font-size: var(--hh-minified-font-size, 12px);
      transform: translateY(-9px);
    }

    :host([height='multiline'][filled='true']) ::slotted(label) {
      transform: translateY(-4px);
    }

    :host([animation='disappear'][filled='true']) ::slotted(label) {
      visibility: hidden;
      transform: translateY(0);
      font-size: inherit;
    }

    :host([animation='disappear']) ::slotted(input) {
      top: 0;
    }

    :host([animation='disappear']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
      padding: var(--hh-left-indent-small, 4px);
      margin-top: 0;
    }

    :host {
      position: relative;
      display: block;
      height: calc(var(--hh-height, 45px) + 2px);
    }

    :host {
      background: transparent;
      border: var(--hh-border-size, 1px) solid var(--hh-color-brand-secondary);
    }

    :host([theme='minimal']) {
      background: transparent;
      border: none;
      border-bottom: var(--hh-border-size, 1px) solid
        var(--hh-color-brand-secondary);
    }

    :host([label='stacked']) {
      border: none;
      height: auto;
    }

    :host([label='stacked']) ::slotted(label),
    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      position: relative;
    }

    :host([label='stacked']) ::slotted(label) {
      height: auto;
      display: block;
      font: var(--hh-label-font);
      margin: var(--hh-label-margin);
      padding-left: 0;
    }

    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      border: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--hh-left-indent, 32px);
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--hh-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--hh-right-indent, 32px);
    }

    ::slotted([slot='icon']) {
      display: flex;
      position: absolute;
      width: var(--hh-icon-width, 23px);
      height: var(--hh-height, 45px);
      z-index: 3;
    }

    :host([icon='right']) ::slotted([slot='icon']) {
      right: var(--hh-icon-right, 10px);
    }

    :host([height='multiline']) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 73px);
    }

    :host([height='compact']),
    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--hh-compact-height, 35px);
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--hh-compact-height, 35px);
    }

    :host {
      transition: all 0.3s;
    }

    :host([mouseover='true']) {
      border-color: var(--hh-color-brand-tertiary);
      background-color: var(--hh-color-reverse);
      box-shadow: 0 0 2rem 0 rgba(0, 0, 0, 0.15);
    }

    :host([disabled]) {
      pointer-events: none;
      color: var(--hh-color-neutral);
      border-color: var(--hh-color-neutral);
    }

    :host([disabled]) ::slotted(label) {
      color: currentColor;
    }

    :host([error]) {
      border-color: var(--hh-color-danger);
    }

    :host([error]) ::slotted(input),
    :host([error]) ::slotted(textarea) {
      color: var(--hh-color-danger);
    }

    :host([success]) {
      border-color: var(--hh-color-success);
    }

    :host([success]) ::slotted(input),
    :host([success]) ::slotted(textarea) {
      color: var(--hh-color-success);
    }
  `
}

customElements.define('hh-input-wrapper', HHInputWrapper)
